// Modules
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { sub } from "date-fns";
// Material-UI
import {
    Avatar,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Stack,
    Tooltip,
    Typography
} from "@mui/material";
import { Group, Edit, Person } from "@mui/icons-material";

// Components
import InitializeLocation from "../../../AppLayout/InitializeLocation";
// Helpers
import { formatDate, formatTime } from "../../../../helpers/dates";
import { getEmail, getName } from "../../../../helpers/userData";
import {
    FREQ_EVERYXWEEKS,
    EVENT_FREE_SLOT,
    EVENT_VACATION,
    FREQ_NONE,
    EVENT_SESSION,
    BOOK_GROUPED_SESSION,
    THERAPIST_ABSENCE_EVENT_TYPES
} from "../../../../helpers/constants";
// Styles
import { gridSpacing } from "../../../../styles/constants";
// Assets
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
// Actions
import { getPatientsSWR } from "@/actions/shared/patientActions";

const EventDetails = ({ isAdmin, locale, event, actions, showRecurrence = true, showCreatedBy = true }) => {
    const {
        type: eventType,
        description,
        end,
        guests,
        start,
        title,
        user,
        createdBy,
        isGroupedSession,
        rrule: recurrence_rrule,
        recurrence
    } = event || {};

    const {
        hasPermission,
        triggerBookEventModal,
        triggerDeleteEventModal,
        therapistSelectionHook,
        triggerUpdateEventModal,
        triggerCreateAbsenceModal,
        triggerUpdateGroupedSessionEventModal,
        triggerCloseModal
    } = actions;

    const isEventType = type => eventType === type;

    const patients = getPatientsSWR({ displaySpinner: false });

    const isTypeFreeSlot = isEventType(EVENT_FREE_SLOT);
    const isTypeVacation = isEventType(EVENT_VACATION);

    const isNotRecurringEvent = recurrence === FREQ_NONE;

    const timeFormatOptions = {};
    const dateFormatOptions = { weekday: "long", month: "long" };

    const therapistName = user?.name ?? getName();
    const therapistEmail = user?.email ?? getEmail();

    // Functions
    const handleEventBook = hasPermission(`book_${eventType}`) ? triggerBookEventModal : null;
    const handleEventDelete = hasPermission(`delete_${eventType}`) ? triggerDeleteEventModal : null;

    const handleEventUpdate = () => {
        if (isAdmin) {
            therapistSelectionHook(user?._id, true, true);
        }

        if (eventType === EVENT_SESSION) {
            return isNotRecurringEvent && triggerUpdateGroupedSessionEventModal();
        }

        if (THERAPIST_ABSENCE_EVENT_TYPES.includes(eventType)) {
            return triggerCreateAbsenceModal(eventType);
        }

        return triggerUpdateEventModal();
    };

    // Determine if the update button should be shown
    const shouldShowUpdateButton =
        hasPermission(BOOK_GROUPED_SESSION) &&
        ((isEventType(EVENT_SESSION) && isNotRecurringEvent) || !isEventType(EVENT_SESSION)) &&
        (hasPermission(`edit_${eventType}`) || hasPermission(`edit_grouped_${eventType}`));

    return (
        <InitializeLocation adapter="date" locale={locale}>
            <DialogTitle>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <Grid container spacing={gridSpacing} alignItems="center">
                            <Grid item>{title || <FormattedMessage id="event" />}</Grid>
                            {shouldShowUpdateButton && (
                                <Grid item sx={{ ml: "auto" }}>
                                    <Tooltip title={<FormattedMessage id="update-event" />}>
                                        <IconButton onClick={handleEventUpdate} size="large" data-testid="update-event">
                                            <Edit color="primary" />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container spacing={gridSpacing} alignItems="center">
                            <Grid item>
                                <Typography variant="body1">
                                    {isTypeVacation
                                        ? `${formatDate(start, dateFormatOptions, locale.code)} - ${formatDate(
                                              /**
                                               * ! allDay event
                                               * ? For all-day events, the chosen end date is considered exclusive, indicating that it will not be
                                               * ? included in the calculation. Therefore, a workaround is required.
                                               **/
                                              sub(end, { days: 1 }),
                                              dateFormatOptions,
                                              locale.code
                                          )}`
                                        : formatDate(start, dateFormatOptions, locale.code)}
                                </Typography>
                            </Grid>
                            {!isTypeVacation && (
                                <Grid item>
                                    <Typography variant="body1">
                                        {`${formatTime(start, timeFormatOptions, locale.code)} - ${formatTime(
                                            end,
                                            timeFormatOptions,
                                            locale.code
                                        )}`}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                    {recurrence && recurrence_rrule && showRecurrence && (
                        <Grid item xs={12}>
                            <Grid container spacing={gridSpacing} alignItems="center">
                                <Grid item>
                                    <Typography variant="body1">
                                        {recurrence === FREQ_EVERYXWEEKS ? (
                                            <>
                                                <FormattedMessage id="every" />
                                                {` ${recurrence_rrule.interval || 1} `}
                                                <FormattedMessage id="weeks" />
                                            </>
                                        ) : (
                                            <FormattedMessage id={recurrence} />
                                        )}
                                    </Typography>
                                </Grid>
                                {recurrence_rrule?.until && (
                                    <Grid item>
                                        <Typography variant="body1">
                                            <>
                                                (
                                                <FormattedMessage id="until" />
                                                {` ${formatDate(
                                                    recurrence_rrule?.until,
                                                    dateFormatOptions,
                                                    locale.code
                                                )}`}
                                                )
                                            </>
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                    )}
                    {isTypeFreeSlot && showCreatedBy && (
                        <Grid item xs={12}>
                            <Grid container spacing={gridSpacing} alignItems="center">
                                <Grid item>
                                    <Typography variant="body1">
                                        <FormattedMessage id="created-by" />: {createdBy}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 3 }}>
                <Grid container spacing={1}>
                    {description && (
                        <Grid item xs={12}>
                            <Grid container spacing={gridSpacing} alignItems="center">
                                <Grid item>
                                    <DescriptionOutlinedIcon />
                                </Grid>
                                <Grid item>
                                    <Typography variant="body1">{description}</Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                    {guests && guests.length > 0 && (
                        <>
                            <Grid item xs={6}>
                                <Grid item>
                                    <Grid container spacing={gridSpacing} alignItems="center">
                                        <Grid item>
                                            <Person />
                                        </Grid>
                                        <Grid item>
                                            <Grid item>
                                                <Typography align="left" variant="body1">
                                                    <FormattedMessage id="therapist" />
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid container spacing={2} sx={{ pl: "48px", pt: "12px" }}>
                                    <Grid item xs={12}>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item>
                                                <Avatar sx={{ width: 32, height: 32, fontSize: "18px" }}>
                                                    {(therapistName || therapistEmail || "?")[0]}
                                                </Avatar>
                                            </Grid>
                                            <Grid item xs zeroMinWidth>
                                                <Typography align="left" variant="h6">
                                                    {user?.name || getName()}
                                                </Typography>
                                                <Typography align="left" variant="body1" noWrap>
                                                    {user?.email || getEmail()}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={6}>
                                <Grid item>
                                    <Grid container spacing={gridSpacing} alignItems="center">
                                        <Grid item>
                                            <Group />
                                        </Grid>
                                        <Grid item>
                                            <Grid item>
                                                <Typography align="left" variant="body1">
                                                    <FormattedMessage id={isGroupedSession ? "patients" : "patient"} />
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                {guests.length > 0 && (
                                    <Grid container spacing={2} sx={{ pl: "48px", pt: "12px" }}>
                                        {guests.map((guest, index) => (
                                            <Grid item xs={12} key={index}>
                                                <Grid container spacing={2} alignItems="center">
                                                    <Grid item>
                                                        <Avatar sx={{ width: 32, height: 32, fontSize: "18px" }}>
                                                            {(guest?.name || guest?.email || "?")[0]}
                                                        </Avatar>
                                                    </Grid>
                                                    <Grid item xs zeroMinWidth>
                                                        <Typography align="left" variant="h6">
                                                            {
                                                                patients.find(patient => patient.email === guest?.email)
                                                                    ?.child?.name
                                                            }
                                                        </Typography>
                                                        <Typography align="left" variant="h6">
                                                            (<FormattedMessage id="parent" />: {guest?.name})
                                                        </Typography>
                                                        <Typography align="left" variant="body1" noWrap>
                                                            {guest?.email}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}
                            </Grid>
                        </>
                    )}
                </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
                <Grid container justifyContent="space-between" alignItems="center">
                    <Grid item>
                        {handleEventDelete && (
                            <Tooltip title={<FormattedMessage id="delete" />}>
                                <IconButton
                                    onClick={() => handleEventDelete(event)}
                                    size="large"
                                    data-testid="delete-event"
                                >
                                    <DeleteIcon color="error" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Grid>
                    <Grid item>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Button type="button" variant="outlined" onClick={triggerCloseModal}>
                                <FormattedMessage id="cancel" />
                            </Button>
                            {handleEventBook && (
                                <Button
                                    type="button"
                                    variant="contained"
                                    onClick={() => handleEventBook(event)}
                                    data-testid="book-event"
                                >
                                    <FormattedMessage id="book" />
                                </Button>
                            )}
                        </Stack>
                    </Grid>
                </Grid>
            </DialogActions>
        </InitializeLocation>
    );
};

EventDetails.propTypes = {
    isAdmin: PropTypes.bool,
    locale: PropTypes.object,
    event: PropTypes.object,
    actions: PropTypes.shape({
        hasPermission: PropTypes.func,
        triggerBookEventModal: PropTypes.func,
        triggerDeleteEventModal: PropTypes.func,
        therapistSelectionHook: PropTypes.func,
        triggerUpdateEventModal: PropTypes.func,
        triggerCreateAbsenceModal: PropTypes.func,
        triggerUpdateGroupedSessionEventModal: PropTypes.func,
        triggerCloseModal: PropTypes.func
    })
};

export default EventDetails;
